import React, { useEffect, useReducer } from 'react';
import { Row, Col, Container, Table } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { withHeaderMessages } from '../higherOrderComponents/WithHeaderMessages';
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from '../lib/components/LoadingSpinner';
import { Currency } from '../lib/components/Currency';
import { ContractLayout } from './ContractLayout';

const CombinedContractDisbursementQuote = (props) => {

    const initialState = {
        isLoading: true,
        isLoadingQuotes: true,
        quotes: [],
        contracts: [],
        contractNumbers: []
    }

    const reducer = (state, action) => {
        switch (action.type) {
            case 'contracts':
                return {
                    ...state,
                    contracts: action.payload,
                    isLoading: false
                }
            case 'contractNumbers':
                return {
                    ...state,
                    contractNumbers: action.payload,
                }
            case 'quotes':
                return {
                    ...state,
                    quotes: action.payload,
                    isLoadingQuotes: false
                }
            case 'isLoadingQuotes':
                return {
                    ...state,
                    isLoadingQuotes: action.payload
                }
            case 'isLoading':
                return {
                    ...state,
                    isLoading: action.payload
                }
            case 'hideLoader': 
                return {
                    ...state,
                    isLoading: false
                }
            default:
                throw new Error();
        }
    }

    const [combinedContractState, dispatch] = useReducer(reducer, initialState);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        props.header.setHeader("combinedContractQuote");
        if (!location.state || !location.state.purchases || location.state.purchases.length === 0) {
            navigate('/contracts');
        }
        
        const contractNumbers = [...new Set(location.state.purchases.map(p => p.contractNumber))];
        dispatch({ type: 'contractNumbers', payload: contractNumbers });

        Promise.all(
            contractNumbers.map(n => 
                props.api.get(`/api/contract/${n}`)
                    .then(response => {
                        if (response.status === 200) { 
                            return response.data;
                        }
                    })
            )
        )
        .catch(() => dispatch({ type: 'isLoading', payload: false }))
        .then(contracts => {
            dispatch({ type: 'contracts', payload: contracts });
        });

    }, [])

    useEffect(() => {
        const contractNumbers = [...new Set(location.state.purchases.map(p => p.contractNumber))];
        Promise.all(
            contractNumbers.map(contractNumber => {
                const postBody = {
                    contractNumber: contractNumber,
                    reason: '',
                    terminationDate: null,
                    valueOfService: '',
                    includedPurchaseIds: location.state.purchases.filter(p => p.contractNumber === contractNumber).map(p => p.purchaseId) 
                };

                return props.api.post('/api/contract/disbursement-quote', postBody)
                    .then(response => {
                        if (response.status === 200) { 
                            return response.data;
                        }
                    });
                }
            )
        )
        .catch(() => dispatch({ type: 'isLoadingQuotes', payload: false }))
        .then(quotes => {
            dispatch({ type: 'quotes', payload: quotes });
        });
    }, [])

    const renderContract = (contractNumbers, contractNumber, quote, status, i) => {
        return (
            <ContractLayout label={"Contract " + contractNumber} contractNumber={contractNumbers} pageTitle="Distribution Quote" contractStatus={status}
                isCombined={true} displayButtons={i === 0}>
                <>
                    <Row className='border-top border-3 py-3' />
                    <Container>
                        <Col sm={7}>
                            <table className="dq-table table table-borderless">
                                <thead>
                                    <tr>
                                    <th></th>
                                    <th style={{fontWeight: "bold"}}>Principal</th>
                                    <th style={{fontWeight: "bold"}}>Income</th>
                                    <th style={{fontWeight: "bold"}}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                    <td style={{color: "#6C6C6D"}}>
                                        Total Guaranteed
                                    </td>
                                    <td>
                                        <Currency amount={quote.guaranteedPrincipal} />
                                    </td>
                                    <td>
                                        <Currency amount={quote.guaranteedIncome} />
                                    </td>
                                    <td>
                                        <Currency amount={quote.guaranteedPrincipal + quote.guaranteedIncome} />
                                    </td>
                                    </tr>
                                    <tr>
                                    <td style={{color: "#6C6C6D"}}>
                                        Total Non-Guaranteed
                                    </td>
                                    <td>
                                        <Currency amount={quote.nonGuaranteedPrincipal} />
                                    </td>
                                    <td>
                                        <Currency amount={quote.nonGuaranteedIncome} />
                                    </td>
                                    <td>
                                        <Currency amount={quote.nonGuaranteedPrincipal + quote.nonGuaranteedIncome} />
                                    </td>
                                    </tr>
                                    <tr>
                                    <td style={{color: "#6C6C6D"}}>
                                        Contract Total
                                    </td>
                                    <td>
                                        <span style={{color: "#d92328"}}><Currency amount={quote.guaranteedPrincipal + quote.nonGuaranteedPrincipal} /></span>
                                    </td>
                                    <td>
                                        <span style={{color: "#d92328"}}><Currency amount={quote.guaranteedIncome + quote.nonGuaranteedIncome} /></span>
                                    </td>
                                    <td>
                                        <span style={{color: "#d92328"}}>
                                            <Currency amount={quote.guaranteedPrincipal + quote.nonGuaranteedPrincipal + quote.guaranteedIncome + quote.nonGuaranteedIncome} /></span>
                                    </td>
                                    </tr>
                                </tbody>
                            </table>
                            <br />

                            <Table className="table-sm dq2-table table-borderless">
                                <thead>
                                <tr>
                                    <th style={{fontWeight: "bold"}}>Who to Pay</th>
                                    <th style={{fontWeight: "bold"}}>Principal</th>
                                    <th style={{fontWeight: "bold"}}>Income</th>
                                    <th style={{fontWeight: "bold"}}>Payment Method</th>
                                </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Provider: {quote.providerCode} - {quote.providerName}</td>
                                        <td><Currency amount={quote.principalToProvider} /></td>
                                        <td><Currency amount={quote.incomeToProvider} /></td>
                                        <td>{quote.providerPaymentMethod}</td>
                                    </tr>
                                    {(quote.principalToBeneficiaries !== 0 || quote.incomeToBeneficiaries !== 0) &&
                                    <tr>
                                        <td>Beneficiary: {quote.beneficiary}</td>
                                        <td><Currency amount={quote.principalToBeneficiaries} /></td>
                                        <td><Currency amount={quote.incomeToBeneficiaries} /></td>
                                        <td>{quote.beneficiaryPaymentMethod}</td>
                                    </tr>
                                    } 
                                    {(quote.principalToPerpetualCare !== 0 || quote.incomeToPerpetualCare !== 0) &&
                                    <tr>
                                        <td>Perpetual Care</td>
                                        <td><Currency amount={quote.principalToPerpetualCare} /></td>
                                        <td><Currency amount={quote.incomeToPerpetualCare} /></td>
                                        <td>{quote.perpetualCarePaymentMethod}</td>
                                    </tr>
                                    }
                                </tbody>
                            </Table>
                        </Col>
                    </Container>
                </>
            </ContractLayout>
        )
    }

    if (combinedContractState.isLoading ||combinedContractState.isLoadingQuotes) {
        return <LoadingSpinner />;
    }
    const { contracts, contractNumbers, quotes } = combinedContractState;
    const status = contracts.some(c => c.status === "Accepted") ? "Accepted" : "";
    const contractNumbersQueryString = contractNumbers.join(',');
    return (
        <div>
            {contractNumbers.map((contractNumber, i) => {
                const contractQuote = quotes.find(q => q.contractNumber === contractNumber);
                return renderContract(contractNumbersQueryString, contractNumber, contractQuote, status, i);
            })}
        </div>
    );
}

export default withHeaderMessages(WithApiAccess(CombinedContractDisbursementQuote));